/**
 * Design tokens — the single source of truth for the Squid UI.
 *
 * Every colour, radius, spacing step and shadow used by the app is defined
 * here and consumed through the MUI theme. Do not hardcode hex values in
 * components; add a token here and reference it via the theme instead.
 *
 * Palette: ROLLER SKO brand. The three fixed brand values are
 *   ROLLER Blue  #0052FF  (primary action, brand identity)
 *   ROLLER Navy  #0A1128  (deepest canvas)
 *   ROLLER Red   #FF3B30  (alert / unplanned work)
 * Everything else is a ramp derived around those so the UI stays on-brand
 * rather than the stock MUI indigo it shipped with.
 *
 * Structure follows Linear's dark-mode model: elevation is expressed as steps
 * in surface luminance with whisper-thin translucent borders, not as heavy
 * drop shadows — on a near-black canvas, dark-on-dark shadows are invisible.
 */

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

// Neutral ramp — surfaces, borders and text. Cool-toned to sit with navy.
export const neutral = {
  0: '#FFFFFF',
  50: '#F9FAFB',
  100: '#F5F7FA',
  200: '#E5E7EB',
  300: '#D1D5DB',
  400: '#9BA7BD',
  500: '#6B7280',
  600: '#4A5568',
  700: '#2D3748',
  800: '#161E33',
  900: '#0A1128',
  950: '#060B1A',
};

// Brand ramp, built around ROLLER Blue #0052FF (= brand[500]).
export const brand = {
  50: '#EEF4FF',
  100: '#DDE8FF',
  200: '#C8D8FF',
  300: '#8DB0F5',
  400: '#4D8AFF',
  500: '#0052FF', // ROLLER Blue — primary
  600: '#2255EE',
  700: '#1A42BF',
  800: '#143391',
  900: '#0E2463',
};

// Semantic accents. Red is the fixed SKO alert red; the rest are tuned to
// sit beside it without competing with brand blue.
export const accent = {
  red: '#FF3B30', // ROLLER Red
  amber: '#F5A524',
  emerald: '#17C964',
  sky: '#4D8AFF',
  violet: '#7C5CFF',
  teal: '#0FB5AE',
};

// Surfaces for the dark theme, anchored on ROLLER Navy.
// Layered so cards read above the canvas by luminance, not by shadow.
export const surface = {
  canvas: '#0A1128', // ROLLER Navy
  raised: '#111A36',
  overlay: '#182344',
  sunken: '#060B1A',
};

/** Translucent hairline borders — the primary depth cue on dark surfaces. */
export const border = {
  subtle: 'rgba(200, 216, 255, 0.07)',
  standard: 'rgba(200, 216, 255, 0.12)',
  strong: 'rgba(200, 216, 255, 0.18)',
};

// ---------------------------------------------------------------------------
// Work-category colours
// ---------------------------------------------------------------------------
// These drive both charts and category chips. Each has a solid colour plus a
// soft translucent variant for chip/badge backgrounds.
//
// Keys are the six current work types. `Project`, `Learn` and
// `Uncategorized` are retired and deliberately absent; `Unattributed` is the
// honest residual and stays deliberately grey so it never looks like
// attributed work.
export const categoryPalette = {
  // `main` drives charts/dots; `text` is the on-tint label colour, lightened
  // where `main` is too dark to read on a navy-tinted chip (notably the deep
  // ROLLER Blue of Company Projects).
  'Company Projects': { main: '#0052FF', soft: 'rgba(0, 82, 255, 0.20)', text: '#8DB0F5' },
  'Internal Projects': { main: '#4D8AFF', soft: 'rgba(77, 138, 255, 0.18)', text: '#8DB0F5' },
  Toil: { main: '#FFBE4D', soft: 'rgba(245, 165, 36, 0.18)', text: '#FFBE4D' },
  Unplanned: { main: '#FF3B30', soft: 'rgba(255, 59, 48, 0.18)', text: '#FF8A82' },
  'Learn/Levelup': { main: '#17C964', soft: 'rgba(23, 201, 100, 0.18)', text: '#45D87F' },
  // Operational change work (maintenance/upgrade/patch) — the SKO violet
  // accent, kept visually distinct from both Toil (amber) and Internal
  // Projects (blue) since Change tickets are neither routine toil nor
  // project work.
  Change: { main: '#7C5CFF', soft: 'rgba(124, 92, 255, 0.18)', text: '#B8A9FF' },
  Unattributed: { main: '#6B7280', soft: 'rgba(107, 114, 128, 0.18)', text: '#9BA7BD' },
  Other: { main: '#6B7280', soft: 'rgba(107, 114, 128, 0.18)', text: '#9BA7BD' },
};

export const allocationPalette = {
  meetings: '#7C5CFF',
  focus: '#4D8AFF',
  attributed: '#17C964',
  unattributed: '#6B7280',
  absence: '#F5A524',
  inferred: '#8DB0F5',
};

// ---------------------------------------------------------------------------
// Shape, spacing, motion
// ---------------------------------------------------------------------------

export const radius = {
  sm: 6,
  md: 10,
  lg: 14,
  xl: 20,
  pill: 999,
};

/** Base spacing unit in px. All spacing is a multiple of this (8pt grid). */
export const spacingUnit = 8;

export const shadow = {
  sm: '0 1px 2px rgba(6, 11, 26, 0.5)',
  md: '0 4px 14px rgba(6, 11, 26, 0.5)',
  lg: '0 16px 40px rgba(6, 11, 26, 0.6)',
  focus: '0 0 0 3px rgba(0, 82, 255, 0.35)',
};

export const motion = {
  fast: '120ms cubic-bezier(0.4, 0, 0.2, 1)',
  base: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
};

export const layout = {
  /** Width of the persistent navigation rail. */
  sidebarWidth: 248,
  /** Height of the top app bar. */
  headerHeight: 64,
  /** Standard chart body height. */
  chartHeight: 300,
};

export const fontStack = [
  'Inter',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(', ');

export const monoStack = [
  '"JetBrains Mono"',
  'ui-monospace',
  'SFMono-Regular',
  'Menlo',
  'Consolas',
  'monospace',
].join(', ');
